/**
 * Regra de priorização da planilha (aba "Regra Priorização"), com os mesmos critérios e pesos:
 *
 * Aderência à estratégia (pesos 30/40/30):
 *  1. Complexidade do projeto: 3 = solução completa; 2 = média; 1 = pequeno.
 *  2. Utilização de prestadores de serviços (terceiros): 3 = mais de 2; 2 = um; 1 = nenhum.
 *  3. Utilização de vendor (fabricante): 3 = mais de 2; 2 = um; 1 = nenhum.
 * Complexidade da execução (pesos 35/30/20/15):
 *  1. Tempo de implementação: 3 = acima de 12 meses; 2 = de 3 a 9 meses; 1 = menos de 3 meses.
 *  2. Custo total: 3 = acima de R$ 10.000; 2 = entre R$ 5.000 e R$ 10.000; 1 = abaixo de R$ 5.000.
 *  3. Aquisição de equipamentos pela AD: 3 = vários fornecedores; 2 = um fornecedor; 1 = nenhuma.
 *  4. Origem do fornecimento: 3 = internacional; 2 = nacional e importado (3+ fornecedores); 1 = só nacional.
 *
 * Nota = Σ(pontos × peso) / 100. Conferido com a planilha (3,1,1 → 1,60; 2,2,3,3 → 2,35).
 * Classificação (reproduz os 6 exemplos da planilha): Alta com estratégia ≥ 2,0 e execução ≥ 2,5; Média com estratégia
 * ≥ 1,6 ou execução ≥ 2,0; senão Baixa. Os limites ficam em Administração → Configurações.
 */
import { z } from 'zod';

export const PriorityInput = z.object({
  complexidade: z.number().int().min(1).max(3),
  fornecedores: z.number().int().min(1).max(3),
  vendor: z.number().int().min(1).max(3),
  tempo: z.number().int().min(1).max(3),
  custo: z.number().int().min(1).max(3),
  equipamentos: z.number().int().min(1).max(3),
  origem: z.number().int().min(1).max(3),
});
export type PriorityInput = z.infer<typeof PriorityInput>;

export const PRIORITY_CRITERIA: { key: keyof PriorityInput; group: 'estrategia' | 'execucao'; label: string; weight: number; options: [string, string, string] }[] = [
  { key: 'complexidade', group: 'estrategia', label: 'Complexidade do projeto', weight: 30, options: ['Pequeno, menor complexidade', 'Complexidade média', 'Solução completa, maior complexidade'] },
  { key: 'fornecedores', group: 'estrategia', label: 'Utilização de prestadores de serviços', weight: 40, options: ['Nenhum prestador', 'Um prestador', 'Mais de 2 prestadores'] },
  { key: 'vendor', group: 'estrategia', label: 'Utilização de vendor (fabricante)', weight: 30, options: ['Nenhum vendor', 'Um vendor', 'Mais de 2 vendors'] },
  { key: 'tempo', group: 'execucao', label: 'Tempo de implementação após aprovado', weight: 35, options: ['Menos de 3 meses', 'De 3 a 9 meses', 'Acima de 12 meses'] },
  { key: 'custo', group: 'execucao', label: 'Custo total do projeto', weight: 30, options: ['Abaixo de R$ 5.000', 'Entre R$ 5.000 e R$ 10.000', 'Acima de R$ 10.000'] },
  { key: 'equipamentos', group: 'execucao', label: 'Aquisição de equipamentos pela AD Digital', weight: 20, options: ['Nenhum equipamento', 'Um único fornecedor', 'Diversos fornecedores'] },
  { key: 'origem', group: 'execucao', label: 'Origem do fornecimento', weight: 15, options: ['Somente nacional', 'Nacional e importado (3+ fornecedores)', 'Internacional'] },
];

export interface PriorityRule { highStrategy: number; highExecution: number; mediumStrategy: number; mediumExecution: number }
export const DEFAULT_PRIORITY_RULE: PriorityRule = { highStrategy: 2.0, highExecution: 2.5, mediumStrategy: 1.6, mediumExecution: 2.0 };

export type PriorityLevel = 'baixa' | 'media' | 'alta';
export const PRIORITY_LABEL: Record<PriorityLevel, string> = { baixa: 'Baixa', media: 'Média', alta: 'Alta' };

export function priority(i: PriorityInput, rule: PriorityRule = DEFAULT_PRIORITY_RULE): { estrategia: number; execucao: number; level: PriorityLevel } {
  const score = (g: 'estrategia' | 'execucao') =>
    Math.round(PRIORITY_CRITERIA.filter((c) => c.group === g).reduce((s, c) => s + i[c.key] * c.weight, 0)) / 100;
  const estrategia = score('estrategia');
  const execucao = score('execucao');
  const level: PriorityLevel = estrategia >= rule.highStrategy && execucao >= rule.highExecution ? 'alta'
    : estrategia >= rule.mediumStrategy || execucao >= rule.mediumExecution ? 'media' : 'baixa';
  return { estrategia, execucao, level };
}
